import { Injectable } from '@angular/core';
import { Article } from '@article/const';
import { CityService } from '@city/feature';
import { CustomBasePageMetaResolver } from '@core/feature';
import {
  BreadcrumbMeta,
  CanonicalPageResolver,
  CanonicalUrlOptions,
  PageBreadcrumbResolver,
  PageDescriptionResolver,
  PageLinkService,
  PageMetaResolver,
  PageRobotsMeta,
  PageRobotsResolver,
  PageTitleResolver,
  PageTypeResolver,
  SemanticPathService,
  WindowRef,
} from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { ArticleService } from '../services';

@Injectable({
  providedIn: 'root',
})
export class ArticlePageMetaResolver
  extends PageMetaResolver
  implements
    PageTitleResolver,
    PageBreadcrumbResolver,
    PageDescriptionResolver,
    PageRobotsResolver,
    CanonicalPageResolver,
    PageTypeResolver
{
  pageTemplate = 'ArticlePageTemplate';

  private articlesBreadcrumbs$ = of([
    { label: 'Список статей', link: this.semanticPathService.get('articleList') },
  ] as BreadcrumbMeta[]);
  private articleDetailBreadcrumbs$ = this.resolveHeading().pipe(map((name) => [{ label: name }] as BreadcrumbMeta[]));

  constructor(
    private basePageMetaResolver: CustomBasePageMetaResolver,
    private winRef: WindowRef,
    private pageLinkService: PageLinkService,
    private cityService: CityService,
    private articleService: ArticleService,
    private semanticPathService: SemanticPathService,
  ) {
    super();
  }

  resolveHeading(): Observable<string | undefined> {
    return this.articleService.getCurrentArticle().pipe(
      filter(Boolean),
      map((article: Article) => article.name),
    );
  }

  resolveTitle(): Observable<string | undefined> {
    return this.articleService.getCurrentArticle().pipe(
      filter(Boolean),
      map((article: Article) => article.metatitle || article.name),
    );
  }

  resolveBreadcrumbs(): Observable<BreadcrumbMeta[] | undefined> {
    return combineLatest({
      breadcrumbs: this.basePageMetaResolver.resolveBreadcrumbs(),
      articlesBreadcrumbs: this.articlesBreadcrumbs$,
      articleDetailBreadcrumbs: this.articleDetailBreadcrumbs$,
    }).pipe(
      map(({ breadcrumbs, articlesBreadcrumbs, articleDetailBreadcrumbs }) => [
        ...breadcrumbs.slice(0, 1),
        ...articlesBreadcrumbs,
        ...articleDetailBreadcrumbs,
      ]),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  resolveDescription(): Observable<string | undefined> {
    return this.articleService.getCurrentArticle().pipe(
      filter(Boolean),
      map((article: Article) => article.description || article.shortDescription),
    );
  }

  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.basePageMetaResolver.resolveRobots();
  }

  resolveCanonicalUrl(options?: CanonicalUrlOptions): Observable<string> {
    return this.cityService.baseStores$.pipe(
      switchMap((baseStoreList) => {
        const pathNameList = this.winRef.location.href
          .replace(this.winRef.location.origin, '')
          .split('/')
          .filter(Boolean);
        const city = baseStoreList.map((baseStore) => baseStore.code).find((code) => code === pathNameList[0]);
        if (city) {
          const canonicalPathWithoutCity = pathNameList.slice(1).join('/');

          return of(
            this.pageLinkService.getCanonicalUrl(options, this.winRef.location.origin + '/' + canonicalPathWithoutCity),
          );
        }

        return this.basePageMetaResolver.resolveCanonicalUrl(options);
      }),
    );
  }

  resolveType(): Observable<string> {
    return this.basePageMetaResolver.resolveType();
  }
}
